// @ts-nocheck
/**
 * Coffee Quiz - Interactive quiz to recommend coffee products
 * Features: Multi-step navigation, session storage, GA4 tracking, recommendation engine
 */

// Moxie subscription app (suscripciones-moxie.onrender.com) applies this
// discount via a Shopify Function that watches for the `Suscripción: X% OFF`
// line-item property. We mirror the percentage here so the in-quiz price
// preview matches what Shopify will compute at the cart.
const SUBSCRIPTION_DISCOUNT = 0.05;

class CoffeeQuiz {
  constructor() {
    this.modal = document.getElementById('coffee-quiz-modal');
    
    // Check if email capture is enabled
    this.emailCaptureEnabled = this.modal?.getAttribute('data-email-capture-enabled') !== 'false';
    this.klaviyoListId = this.modal?.getAttribute('data-klaviyo-list-id') || '';
    this.emailSubmitted = this.hasEmailBeenSubmitted();
    
    // Dynamically detect screens from DOM (supports block reordering in theme editor)
    this.screens = this.detectScreensFromDOM();
    
    this.currentScreenIndex = 0;
    this.answers = this.loadAnswers();
    this.products = [];
    this.recommendedProduct = null;
    this.isAdvancing = false; // Debounce flag to prevent double-advancing screens
    this.sliderData = this.loadSliderData();
    this.boundHandlers = {}; // Store bound handlers for cleanup
    
    this.init();
  }

  init() {
    // Only continue with full initialization if modal exists
    if (!this.modal) {
      console.warn('Coffee Quiz: Modal element not found');
      // Still bind open buttons for when modal becomes available
      this.bindOpenButtons();
      return;
    }
    
    this.products = this.loadProducts();
    this.bindOpenButtons();
    this.bindModalEvents();
    this.bindSliderEvents();
    this.bindBodySkipLink();
    this.bindEmailForm();
    this.updateProgress();
    this.restoreState();
  }
  
  /**
   * Detect screens from DOM in their current order
   * This allows screens to be reordered via theme editor blocks
   */
  detectScreensFromDOM() {
    if (!this.modal) {
      // Fallback to default order if modal not found
      return ['intro', 'experience', 'brew-method', 'flavors', 'how-you-take-it', 'body-preference', 'cups-per-day', 'email-capture', 'results'];
    }
    
    const screenElements = this.modal.querySelectorAll('[data-quiz-screen]');
    const screens = [];
    
    screenElements.forEach(el => {
      const screenName = el.getAttribute('data-quiz-screen');
      if (screenName) {
        // Skip email-capture if disabled
        if (screenName === 'email-capture' && !this.emailCaptureEnabled) {
          return;
        }
        screens.push(screenName);
      }
    });
    
    return screens;
  }
  
  /**
   * Load slider configuration data
   */
  loadSliderData() {
    // Simple 1-8 cups slider
    return {
      min: 1,
      max: 8
    };
  }
  
  /**
   * Bind open quiz buttons using event delegation
   */
  bindOpenButtons() {
    // Only bind once globally using a flag on document
    if (document._coffeeQuizOpenBound) return;
    document._coffeeQuizOpenBound = true;
    
    // Use event delegation on document for reliability
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-open-coffee-quiz]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        // Always get fresh reference to quiz instance
        if (window.coffeeQuiz) {
          window.coffeeQuiz.open();
        }
      }
    });
    
    // Handle hash links (e.g., #coffee-quiz)
    this.bindHashTrigger();
  }
  
  /**
   * Bind hash URL trigger for opening quiz via links like #coffee-quiz
   */
  bindHashTrigger() {
    const QUIZ_HASH = '#coffee-quiz';
    
    // Check hash on page load
    const checkAndOpenFromHash = () => {
      if (window.location.hash === QUIZ_HASH) {
        // Small delay to ensure page is ready
        setTimeout(() => {
          if (window.coffeeQuiz) {
            window.coffeeQuiz.open();
            // Clear the hash without scrolling
            history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        }, 100);
      }
    };
    
    // Check on initial load
    checkAndOpenFromHash();
    
    // Listen for hash changes (e.g., clicking a #coffee-quiz link on the same page)
    window.addEventListener('hashchange', () => {
      if (window.location.hash === QUIZ_HASH && window.coffeeQuiz) {
        window.coffeeQuiz.open();
        // Clear the hash without scrolling
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    });
    
    // Also intercept clicks on links with #coffee-quiz href
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href="#coffee-quiz"], a[href$="#coffee-quiz"]');
      if (link) {
        e.preventDefault();
        if (window.coffeeQuiz) {
          window.coffeeQuiz.open();
        }
      }
    });
  }

  /**
   * Load products data from JSON script tag
   */
  loadProducts() {
    const dataEl = document.getElementById('coffee-quiz-products');
    if (!dataEl) return [];
    
    try {
      return JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Coffee Quiz: Failed to parse products:', e);
      return [];
    }
  }

  /**
   * Load saved answers from session storage
   */
  loadAnswers() {
    try {
      const saved = sessionStorage.getItem('coffee_quiz');
      if (saved) {
        const data = JSON.parse(saved);
        return data.answers || {};
      }
    } catch (e) {
      console.error('Failed to load quiz answers:', e);
    }
    return {};
  }

  /**
   * Save answers and current step to session storage
   */
  saveState() {
    try {
      sessionStorage.setItem('coffee_quiz', JSON.stringify({
        currentStep: this.currentScreenIndex,
        answers: this.answers
      }));
    } catch (e) {
      console.error('Failed to save quiz state:', e);
    }
  }

  /**
   * Restore quiz state from session storage
   */
  restoreState() {
    // Restore selected options
    Object.entries(this.answers).forEach(([key, value]) => {
      // Special handling for cups slider
      if (key === 'cupsPerDay') {
        const slider = this.modal.querySelector('[data-cups-slider]');
        const hiddenInput = this.modal.querySelector('[data-quiz-cups-value]');
        if (slider && hiddenInput) {
          // Value is now a number 1-8
          const numValue = parseInt(value) || 1;
          slider.value = numValue;
          hiddenInput.value = numValue;
          // Trigger visual update
          slider.dispatchEvent(new Event('input'));
        }
        return;
      }
      
      const container = this.modal.querySelector(`[data-quiz-options="${key}"]`);
      if (!container) return;
      
      if (Array.isArray(value)) {
        // Multi-select
        value.forEach(v => {
          const input = container.querySelector(`input[value="${v}"]`);
          if (input) {
            input.checked = true;
            input.closest('.coffee-quiz__option-list-item, .coffee-quiz__option-grid-item, .coffee-quiz__option-horizontal-item, .coffee-quiz__scale-option')?.classList.add('is-selected');
          }
        });
      } else {
        // Single select
        const input = container.querySelector(`input[value="${value}"]`);
        if (input) {
          input.checked = true;
          input.closest('.coffee-quiz__option-list-item, .coffee-quiz__option-grid-item, .coffee-quiz__option-horizontal-item, .coffee-quiz__scale-option')?.classList.add('is-selected');
        }
      }
      
      // Set wasChecked attribute for all inputs in this container
      // This enables re-clicking a pre-selected option to advance
      container.querySelectorAll('input').forEach(input => {
        input.dataset.wasChecked = input.checked ? 'true' : 'false';
      });
    });
    
    // In editor mode, restore the current screen position (if enabled)
    if (window.Shopify && window.Shopify.designMode && this.isEnabledInEditor()) {
      this.restoreEditorPosition();
    }
  }
  
  /**
   * Check if quiz is enabled in theme editor
   */
  isEnabledInEditor() {
    if (!window.Shopify?.designMode) return true;
    if (!this.modal) return false;
    const enableInEditor = this.modal.getAttribute('data-enable-in-editor');
    return enableInEditor !== 'false';
  }
  
  /**
   * Restore screen position in theme editor mode
   */
  restoreEditorPosition() {
    // Skip if disabled in theme editor
    if (!this.isEnabledInEditor()) {
      console.log('Coffee Quiz: Editor restore disabled');
      return;
    }
    
    // First check for preview screen setting (takes priority)
    const previewScreen = this.modal.dataset.previewScreen;
    if (previewScreen && previewScreen !== 'none') {
      const screenIndex = this.screens.indexOf(previewScreen);
      if (screenIndex !== -1) {
        if (!this.modal.open) {
          this.modal.showModal();
          document.body.classList.add('coffee-quiz-open');
        }
        this.goToScreen(screenIndex);
        return;
      }
    }
    
    // Otherwise try to restore saved position
    try {
      const saved = sessionStorage.getItem('coffee_quiz_editor_position');
      if (saved) {
        const savedStep = parseInt(saved);
        if (!isNaN(savedStep) && savedStep >= 0 && savedStep < this.screens.length) {
          if (!this.modal.open) {
            this.modal.showModal();
            document.body.classList.add('coffee-quiz-open');
          }
          this.goToScreen(savedStep);
        }
      }
    } catch (e) {
      // Ignore storage errors
    }
  }
  
  /**
   * Save editor position separately (just the screen index)
   */
  saveEditorPosition() {
    if (window.Shopify && window.Shopify.designMode) {
      try {
        sessionStorage.setItem('coffee_quiz_editor_position', this.currentScreenIndex.toString());
      } catch (e) {
        // Ignore storage errors
      }
    }
  }

  /**
   * Bind modal-specific event listeners
   */
  bindModalEvents() {
    // Close button
    this.modal.querySelector('[data-quiz-close]')?.addEventListener('click', () => {
      this.close();
    });

    // Back button
    this.modal.querySelector('[data-quiz-back]')?.addEventListener('click', () => {
      this.goBack();
    });

    // Start button
    this.modal.querySelector('[data-quiz-start]')?.addEventListener('click', () => {
      this.goToScreen(1);
      this.trackEvent('quiz_started', { quiz_name: 'coffee_quiz' });
    });

    // Continue buttons
    this.modal.querySelectorAll('[data-quiz-continue]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.goNext();
      });
    });

    // Option selections
    this.modal.querySelectorAll('[data-quiz-options]').forEach(container => {
      this.bindOptionEvents(container);
    });

    // Size options (results page)
    this.modal.querySelectorAll('[data-subscription-size], [data-onetime-size]').forEach(btn => {
      btn.addEventListener('click', () => this.handleSizeSelection(btn));
    });

    // Quantity options (results page)
    this.modal.querySelectorAll('[data-subscription-quantity], [data-onetime-quantity]').forEach(btn => {
      btn.addEventListener('click', () => this.handleQuantitySelection(btn));
    });

    // Grind selection
    this.modal.querySelectorAll('[data-subscription-grind], [data-onetime-grind]').forEach(select => {
      select.addEventListener('change', () => this.updatePricing());
    });

    // Frequency selection
    this.modal.querySelector('[data-subscription-frequency]')?.addEventListener('change', () => {
      this.updatePricing();
    });

    // Add to cart buttons - prevent any default behavior
    this.modal.querySelector('[data-add-subscription]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.addToCart('subscription');
    });

    this.modal.querySelector('[data-add-onetime]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.addToCart('onetime');
    });
    
    // Simple add to cart button (for simplified results page)
    this.modal.querySelector('[data-quiz-add-to-cart]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.addToCart('onetime');
    });
    
    // Restart quiz button
    this.modal.querySelector('[data-quiz-restart]')?.addEventListener('click', () => {
      this.restartQuiz();
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.open) {
        this.close();
      }
    });

    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.close();
      }
    });
    
    // Focus trap - keep focus within modal
    this.modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      
      const focusableElements = this.modal.querySelectorAll(
        'button:not([disabled]):not([hidden]):not([aria-hidden="true"]), ' +
        '[href]:not([hidden]), ' +
        'input:not([disabled]):not([type="hidden"]):not([hidden]), ' +
        'select:not([disabled]):not([hidden]), ' +
        'textarea:not([disabled]):not([hidden]), ' +
        '[tabindex]:not([tabindex="-1"]):not([hidden]):not([aria-hidden="true"])'
      );
      
      const visibleFocusable = Array.from(focusableElements).filter(el => {
        return el.offsetParent !== null && !el.closest('[aria-hidden="true"]') && !el.closest('[hidden]');
      });
      
      if (visibleFocusable.length === 0) return;
      
      const firstFocusable = visibleFocusable[0];
      const lastFocusable = visibleFocusable[visibleFocusable.length - 1];
      
      if (e.shiftKey) {
        // Shift + Tab: go backwards
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab: go forwards
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    });
  }

  /**
   * Bind events for option containers (supports list, grid, and horizontal layouts)
   */
  bindOptionEvents(container) {
    const isMultiSelect = container.dataset.multiSelect === 'true';
    const maxSelections = parseInt(container.dataset.maxSelections) || Infinity;
    const autoAdvance = container.dataset.autoAdvance === 'true';
    const optionKey = container.dataset.quizOptions;

    container.querySelectorAll('input').forEach(input => {
      // For single-select with auto-advance, also handle clicks on already-selected options
      if (!isMultiSelect && autoAdvance) {
        const item = input.closest('.coffee-quiz__option-list-item, .coffee-quiz__option-grid-item, .coffee-quiz__option-horizontal-item, .coffee-quiz__scale-option');
        item?.addEventListener('click', (e) => {
          // Only handle if the input was already checked BEFORE this click
          // We check via a data attribute we set on the input
          const wasAlreadyChecked = input.dataset.wasChecked === 'true';
          
          if (wasAlreadyChecked && input.checked) {
            // Prevent double-advance with debounce
            if (this.isAdvancing) return;
            this.isAdvancing = true;
            
            setTimeout(() => {
              this.goNext();
              this.isAdvancing = false;
            }, 300);
          }
        });
      }
      
      input.addEventListener('change', () => {
        const item = input.closest('.coffee-quiz__option-list-item, .coffee-quiz__option-grid-item, .coffee-quiz__option-horizontal-item, .coffee-quiz__scale-option');
        
        if (isMultiSelect) {
          // Handle multi-select
          if (input.checked) {
            const checkedCount = container.querySelectorAll('input:checked').length;
            if (checkedCount > maxSelections) {
              input.checked = false;
              return;
            }
            item?.classList.add('is-selected');
          } else {
            item?.classList.remove('is-selected');
          }
          
          // Update answers array
          const checked = Array.from(container.querySelectorAll('input:checked')).map(i => i.value);
          this.answers[optionKey] = checked;
          
          // Enable/disable continue button
          const continueBtn = this.getCurrentScreen().querySelector('[data-quiz-continue]');
          if (continueBtn) {
            continueBtn.disabled = checked.length === 0;
          }
        } else {
          // Handle single select - support all layout types
          container.querySelectorAll('.coffee-quiz__option-list-item, .coffee-quiz__option-grid-item, .coffee-quiz__option-horizontal-item, .coffee-quiz__scale-option').forEach(i => {
            i.classList.remove('is-selected');
          });
          item?.classList.add('is-selected');
          
          // Mark all inputs in this container to track their checked state for re-click handling
          container.querySelectorAll('input').forEach(i => {
            i.dataset.wasChecked = i.checked ? 'true' : 'false';
          });
          
          this.answers[optionKey] = input.value;
          
          // Track step completion
          this.trackStepCompleted(optionKey, input.value);
          
          // Auto-advance if enabled (with debounce to prevent double-advance)
          if (autoAdvance) {
            if (this.isAdvancing) return;
            this.isAdvancing = true;
            
            setTimeout(() => {
              this.goNext();
              this.isAdvancing = false;
            }, 300);
          }
        }
        
        this.saveState();
      });
    });
  }
  
  /**
   * Bind slider events for cups per day
   */
  bindSliderEvents() {
    const slider = this.modal.querySelector('[data-cups-slider]');
    if (!slider) return;
    
    // Initialize slider after a brief delay to ensure layout is complete
    setTimeout(() => this.updateSliderDisplay(slider.value), 50);
    
    // Handle slider input (fires continuously while dragging)
    slider.addEventListener('input', (e) => {
      this.updateSliderDisplay(e.target.value);
    });
    
    // Handle slider change (for accessibility)
    slider.addEventListener('change', (e) => {
      this.updateSliderDisplay(e.target.value);
    });
    
    // Update icon position on window resize
    window.addEventListener('resize', () => {
      if (slider) {
        this.updateSliderDisplay(slider.value);
      }
    });
  }
  
  /**
   * Update the slider display elements
   */
  updateSliderDisplay(value) {
    const slider = this.modal.querySelector('[data-cups-slider]');
    if (!slider) return;
    
    const numValue = parseFloat(value);
    const roundedValue = Math.round(numValue);
    
    // Update hidden input value (store the rounded number)
    const hiddenInput = this.modal.querySelector('[data-quiz-cups-value]');
    if (hiddenInput) {
      hiddenInput.value = roundedValue;
      this.answers.cupsPerDay = roundedValue;
      this.saveState();
    }
    
    // Update visual value display - show "X" in the number span
    const valueDisplay = this.modal.querySelector('[data-cups-value-display]');
    const labelDisplay = this.modal.querySelector('[data-cups-label]');
    if (valueDisplay) {
      valueDisplay.textContent = roundedValue;
    }
    if (labelDisplay) {
      labelDisplay.textContent = roundedValue === 1 ? 'Taza' : 'Tazas';
    }
    
    // Update ARIA attributes for screen readers
    const valueText = roundedValue === 1 ? '1 cup per day' : `${roundedValue} cups per day`;
    slider.setAttribute('aria-valuenow', roundedValue);
    slider.setAttribute('aria-valuetext', valueText);
    
    // Position the thumb icon overlay
    const thumbIcon = this.modal.querySelector('[data-slider-thumb-icon]');
    if (thumbIcon) {
      const min = parseFloat(slider.min) || 1;
      const max = parseFloat(slider.max) || 8;
      const percent = (numValue - min) / (max - min);
      // Account for thumb width (60px) - icon should be centered in thumb
      const thumbWidth = 60;
      const iconWidth = 32;
      const trackWidth = slider.offsetWidth;
      // Calculate position: slider percentage of track width, adjusted for thumb size
      const position = percent * (trackWidth - thumbWidth) + (thumbWidth - iconWidth) / 2;
      thumbIcon.style.left = `${position}px`;
    }
    
    // Update cup icons - fill based on current value
    this.modal.querySelectorAll('[data-cup-index]').forEach(cup => {
      const cupIndex = parseInt(cup.dataset.cupIndex);
      cup.classList.toggle('is-filled', cupIndex <= roundedValue);
    });
  }
  
  /**
   * Bind body preference skip link ("I Don't Know")
   */
  bindBodySkipLink() {
    const skipLink = this.modal.querySelector('[data-body-skip]');
    if (!skipLink) return;
    
    skipLink.addEventListener('click', () => {
      // Set the answer to "i-dont-know"
      this.answers.bodyPreference = 'i-dont-know';
      this.saveState();
      
      // Track step completion
      this.trackStepCompleted('bodyPreference', 'i-dont-know');
      
      // Advance to next screen
      this.goNext();
    });
  }

  /**
   * Bind email capture form events
   */
  bindEmailForm() {
    const form = this.modal.querySelector('[data-quiz-email-form]');
    const skipBtn = this.modal.querySelector('[data-quiz-email-skip]');
    
    if (!form) return;
    
    const submitBtn = form.querySelector('[data-quiz-email-submit]');
    const emailInput = form.querySelector('input[type="email"]');
    
    // Store original button text for reset
    if (submitBtn) {
      submitBtn.setAttribute('data-original-text', submitBtn.textContent.trim());
      // Start with button disabled
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-disabled', 'true');
    }
    
    // Real-time email validation for button state
    if (emailInput) {
      emailInput.addEventListener('input', () => {
        this.updateEmailSubmitButton();
      });
      
      // Also validate on blur for better UX
      emailInput.addEventListener('blur', () => {
        const email = emailInput.value.trim();
        const emailError = form.querySelector('#quiz-email-error');
        
        if (email && !this.isValidEmail(email)) {
          if (emailError) emailError.textContent = 'Please enter a valid email address';
          emailInput.classList.add('is-error');
          emailInput.setAttribute('aria-invalid', 'true');
        } else {
          if (emailError) emailError.textContent = '';
          emailInput.classList.remove('is-error');
          emailInput.setAttribute('aria-invalid', 'false');
        }
      });
    }
    
    // Handle form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const emailInput = form.querySelector('input[type="email"]');
      const consentCheckbox = form.querySelector('input[name="email_consent"]');
      const submitBtn = form.querySelector('[data-quiz-email-submit]');
      // Use ID selectors for reliability
      const emailError = form.querySelector('#quiz-email-error');
      const consentError = form.querySelector('#quiz-consent-error');
      
      // Clear previous errors
      if (emailError) emailError.textContent = '';
      if (consentError) consentError.textContent = '';
      emailInput.classList.remove('is-error');
      emailInput.setAttribute('aria-invalid', 'false');
      consentCheckbox?.parentElement?.classList.remove('is-error');
      
      // Validate email
      const email = emailInput.value.trim();
      if (!email) {
        if (emailError) emailError.textContent = 'Please enter your email address';
        emailInput.classList.add('is-error');
        emailInput.setAttribute('aria-invalid', 'true');
        emailInput.focus();
        return;
      }
      
      if (!this.isValidEmail(email)) {
        if (emailError) emailError.textContent = 'Please enter a valid email address';
        emailInput.classList.add('is-error');
        emailInput.setAttribute('aria-invalid', 'true');
        emailInput.focus();
        return;
      }
      
      // Validate consent
      if (!consentCheckbox.checked) {
        if (consentError) consentError.textContent = 'Please agree to receive marketing emails';
        consentCheckbox.parentElement.classList.add('is-error');
        consentCheckbox.focus();
        return;
      }
      
      // Show loading state
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.textContent = 'Submitting...';
      
      try {
        // Subscribe to Klaviyo
        await this.subscribeToKlaviyo(email);
        
        // Mark email as submitted so we skip this screen on return
        this.markEmailAsSubmitted();
        this.emailSubmitted = true;
        
        // Track the email capture
        this.trackEvent('quiz_email_captured', { 
          quiz_name: 'coffee_quiz',
          email_captured: true 
        });
        
        // Move to results
        this.goNext();
      } catch (error) {
        console.error('Failed to subscribe to Klaviyo:', error);
        if (emailError) emailError.textContent = 'Something went wrong. Please try again.';
        submitBtn.disabled = false;
        submitBtn.setAttribute('aria-disabled', 'false');
        submitBtn.removeAttribute('aria-busy');
        submitBtn.textContent = 'See My Results';
      }
    });
    
    // Handle skip button
    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        // Mark as submitted so we skip this screen on return
        this.markEmailAsSubmitted();
        this.emailSubmitted = true;
        
        this.trackEvent('quiz_email_skipped', { quiz_name: 'coffee_quiz' });
        this.goNext();
      });
    }
  }

  /**
   * Validate email format
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Update email submit button state based on input validity
   */
  updateEmailSubmitButton() {
    const form = this.modal.querySelector('[data-quiz-email-form]');
    if (!form) return;
    
    const emailInput = form.querySelector('input[type="email"]');
    const submitBtn = form.querySelector('[data-quiz-email-submit]');
    
    if (!emailInput || !submitBtn) return;
    
    const email = emailInput.value.trim();
    const isValid = this.isValidEmail(email);
    
    submitBtn.disabled = !isValid;
    submitBtn.setAttribute('aria-disabled', !isValid ? 'true' : 'false');
  }

  /**
   * Check if email has already been submitted this session
   */
  hasEmailBeenSubmitted() {
    try {
      return sessionStorage.getItem('coffee_quiz_email_submitted') === 'true';
    } catch (e) {
      return false;
    }
  }

  /**
   * Mark email as submitted
   */
  markEmailAsSubmitted() {
    try {
      sessionStorage.setItem('coffee_quiz_email_submitted', 'true');
    } catch (e) {
      console.error('Failed to save email submitted state:', e);
    }
  }

  /**
   * Reset email form to initial state
   */
  resetEmailForm() {
    const form = this.modal.querySelector('[data-quiz-email-form]');
    if (!form) return;
    
    const submitBtn = form.querySelector('[data-quiz-email-submit]');
    const emailInput = form.querySelector('input[type="email"]');
    // Use ID selectors for reliability
    const emailError = form.querySelector('#quiz-email-error');
    const consentError = form.querySelector('#quiz-consent-error');
    
    // Reset button state - disabled until valid email
    if (submitBtn) {
      submitBtn.textContent = submitBtn.getAttribute('data-original-text') || 'See My Results';
      submitBtn.removeAttribute('aria-busy');
      // Check current email value to determine button state
      const hasValidEmail = emailInput && this.isValidEmail(emailInput.value.trim());
      submitBtn.disabled = !hasValidEmail;
      submitBtn.setAttribute('aria-disabled', !hasValidEmail ? 'true' : 'false');
    }
    
    // Clear errors
    if (emailError) emailError.textContent = '';
    if (consentError) consentError.textContent = '';
    if (emailInput) {
      emailInput.classList.remove('is-error');
      emailInput.setAttribute('aria-invalid', 'false');
    }
  }

  /**
   * Subscribe email to Klaviyo
   */
  async subscribeToKlaviyo(email) {
    // Use Klaviyo's identify method to add the email
    if (typeof window.klaviyo !== 'undefined') {
      // Build detailed profile properties
      const profileData = {
        '$email': email,
        '$consent': ['email'],
        // Quiz completion flag
        'Coffee Quiz Completed': true,
        'Coffee Quiz Completed Date': new Date().toISOString(),
        // Individual quiz answers for segmentation
        'Quiz - Coffee Experience': this.answers.experience || '',
        'Quiz - Brew Method': this.answers.brewMethod || '',
        'Quiz - Flavor Preferences': Array.isArray(this.answers.flavors) ? this.answers.flavors.join(', ') : (this.answers.flavors || ''),
        'Quiz - How They Take It': this.answers.howYouTakeIt || '',
        'Quiz - Body Preference': this.answers.bodyPreference || '',
        'Quiz - Cups Per Day': this.answers.cupsPerDay || '',
        // Full answers as JSON for reference
        'Quiz Answers JSON': JSON.stringify(this.answers),
        // Recommendation
        'Quiz Recommended Product': this.recommendedProduct?.title || '',
        'Quiz Recommended Product Handle': this.recommendedProduct?.handle || '',
        'Quiz Recommended Product URL': this.recommendedProduct?.url || ''
      };
      
      // Identify the user with their email and quiz data
      window.klaviyo.identify(profileData);
      
      // Also track as an event for flow triggers
      window.klaviyo.push(['track', 'Coffee Quiz Completed', {
        'Experience Level': this.answers.experience || '',
        'Brew Method': this.answers.brewMethod || '',
        'Flavor Preferences': Array.isArray(this.answers.flavors) ? this.answers.flavors : [this.answers.flavors].filter(Boolean),
        'How They Take It': this.answers.howYouTakeIt || '',
        'Body Preference': this.answers.bodyPreference || '',
        'Cups Per Day': this.answers.cupsPerDay || '',
        'Recommended Product': this.recommendedProduct?.title || '',
        'Recommended Product Handle': this.recommendedProduct?.handle || '',
        'Recommended Product URL': this.recommendedProduct?.url || '',
        'Recommended Product Price': this.recommendedProduct?.price || ''
      }]);
      
      // If a list ID is provided, subscribe to that specific list
      if (this.klaviyoListId) {
        window.klaviyo.push(['subscribe', this.klaviyoListId, { '$email': email }]);
      }
      return true;
    }
    return false;
  }



  /**
   * Handle size button selection
   */
  handleSizeSelection(btn) {
    const isSubscription = btn.hasAttribute('data-subscription-size');
    const selector = isSubscription ? '[data-subscription-size]' : '[data-onetime-size]';
    
    this.modal.querySelectorAll(selector).forEach(b => {
      b.classList.remove('is-selected');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-pressed', 'true');
    
    this.updatePricing();
  }

  /**
   * Handle quantity button selection
   */
  handleQuantitySelection(btn) {
    const isSubscription = btn.hasAttribute('data-subscription-quantity');
    const selector = isSubscription ? '[data-subscription-quantity]' : '[data-onetime-quantity]';
    
    this.modal.querySelectorAll(selector).forEach(b => {
      b.classList.remove('is-selected');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-pressed', 'true');
    
    this.updatePricing();
  }

  /**
   * Open the quiz modal
   */
  open() {
    if (window.Shopify?.designMode && !this.isEnabledInEditor()) {
      return;
    }

    // Try to find modal if not already found
    if (!this.modal) {
      this.modal = document.getElementById('coffee-quiz-modal');
      if (this.modal) {
        // Initialize modal events now that we found it
        this.products = this.loadProducts();
        this.bindModalEvents();
        this.updateProgress();
        this.restoreState();
      }
    }
    
    if (!this.modal) {
      console.error('Coffee Quiz: Modal element not found');
      return;
    }
    
    try {
      // Store the element that triggered the modal for focus return
      this.previousFocus = document.activeElement;
      
      this.modal.showModal();
      document.body.classList.add('coffee-quiz-open');
      
      // Set focus to the first focusable element or the start button
      const startBtn = this.modal.querySelector('[data-quiz-start]');
      if (startBtn) {
        startBtn.focus();
      }
      
      // Announce to screen readers
      this.announceToScreenReader('Coffee quiz opened. ' + (this.modal.querySelector('#quiz-title')?.textContent || 'Find your perfect coffee'));
      
      this.trackEvent('quiz_modal_opened', { quiz_name: 'coffee_quiz' });
    } catch (e) {
      console.error('Coffee Quiz: Error opening modal', e);
    }
  }

  /**
   * Close the quiz modal
   */
  close() {
    this.modal.close();
    document.body.classList.remove('coffee-quiz-open');
    
    // Return focus to the element that opened the modal
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }
    
    this.announceToScreenReader('Coffee quiz closed');
  }
  
  /**
   * Announce message to screen readers via live region
   */
  announceToScreenReader(message) {
    const liveRegion = document.getElementById('quiz-live-region');
    if (liveRegion) {
      liveRegion.textContent = '';
      // Small delay to ensure the change is announced
      setTimeout(() => {
        liveRegion.textContent = message;
      }, 100);
    }
  }

  /**
   * Get current screen element
   */
  getCurrentScreen() {
    return this.modal.querySelector(`[data-quiz-screen="${this.screens[this.currentScreenIndex]}"]`);
  }

  /**
   * Navigate to a specific screen
   * @param {number} index - The screen index to navigate to
   * @param {boolean} isGoingBack - Whether we're navigating backward (optional)
   */
  goToScreen(index, isGoingBack = false) {
    if (index < 0 || index >= this.screens.length) return;
    
    // Skip email capture screen if email was already submitted (only when going forward)
    if (this.screens[index] === 'email-capture' && this.emailSubmitted && !isGoingBack) {
      // Go directly to results instead
      index = this.screens.indexOf('results');
      if (index === -1) index = this.screens.length - 1;
    }
    
    // Reset email form state when showing email capture screen
    if (this.screens[index] === 'email-capture') {
      this.resetEmailForm();
    }
    
    // Hide all screens
    this.modal.querySelectorAll('[data-quiz-screen]').forEach(screen => {
      screen.classList.remove('is-active');
      screen.setAttribute('aria-hidden', 'true');
    });
    
    // Show target screen
    this.currentScreenIndex = index;
    const targetScreen = this.modal.querySelector(`[data-quiz-screen="${this.screens[index]}"]`);
    if (targetScreen) {
      targetScreen.classList.add('is-active');
      targetScreen.setAttribute('aria-hidden', 'false');
    }
    
    // Update back button visibility and accessibility
    const backBtn = this.modal.querySelector('[data-quiz-back]');
    if (backBtn) {
      backBtn.hidden = index === 0;
      backBtn.setAttribute('aria-hidden', index === 0 ? 'true' : 'false');
      backBtn.tabIndex = index === 0 ? -1 : 0;
    }
    
    // Update progress bar
    this.updateProgress();
    
    // Save state
    this.saveState();
    this.saveEditorPosition();
    
    // Calculate recommendation when reaching email capture or results screen
    // Always recalculate to account for any answer changes if user went back
    if (this.screens[index] === 'email-capture' || this.screens[index] === 'results') {
      this.calculateRecommendation();
    }
    
    // If results screen, track completion
    if (this.screens[index] === 'results') {
      this.trackEvent('quiz_completed', {
        quiz_name: 'coffee_quiz',
        answers: this.answers
      });
    }
    
    // Scroll to top of modal content
    this.modal.querySelector('.coffee-quiz__content')?.scrollTo(0, 0);
    
    // Check for multi-select screens with existing selections (e.g., flavors)
    // and enable continue button if there are already selections
    const multiSelectContainer = targetScreen?.querySelector('[data-multi-select="true"]');
    if (multiSelectContainer) {
      const checkedInputs = multiSelectContainer.querySelectorAll('input:checked');
      const continueBtn = targetScreen?.querySelector('[data-quiz-continue]');
      if (continueBtn) {
        continueBtn.disabled = checkedInputs.length === 0;
      }
    }
    
    // Announce screen change to screen readers and manage focus
    const screenTitle = targetScreen?.querySelector('h1, h2')?.textContent;
    if (screenTitle) {
      this.announceToScreenReader(`Step ${index + 1} of ${this.screens.length}: ${screenTitle}`);
    }
    
    // Move focus to the screen heading or first interactive element
    setTimeout(() => {
      const heading = targetScreen?.querySelector('h1, h2');
      const firstFocusable = targetScreen?.querySelector('button, [href], input, select, [tabindex]:not([tabindex="-1"])');
      
      if (heading) {
        // Make heading focusable temporarily
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      } else if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 100);
  }

  /**
   * Go to next screen
   */
  goNext() {
    // Track step completion for multi-select screens
    const currentScreen = this.screens[this.currentScreenIndex];
    if (currentScreen === 'flavors' && this.answers.flavors) {
      this.trackStepCompleted('flavors', this.answers.flavors.join(', '));
    }
    
    this.goToScreen(this.currentScreenIndex + 1);
  }

  /**
   * Go back to previous screen
   */
  goBack() {
    let targetIndex = this.currentScreenIndex - 1;
    
    // When going back, skip the email-capture screen if email was already submitted
    // (go to the screen before email-capture instead)
    if (this.screens[targetIndex] === 'email-capture' && this.emailSubmitted) {
      targetIndex = targetIndex - 1;
    }
    
    if (targetIndex >= 0) {
      this.goToScreen(targetIndex, true); // Pass true to indicate going back
    }
  }

  /**
   * Restart the quiz from the beginning
   */
  restartQuiz() {
    // Clear answers
    this.answers = {};
    this.recommendedProduct = null;
    this.emailSubmitted = false;
    
    // Clear session storage
    sessionStorage.removeItem('coffee_quiz');
    sessionStorage.removeItem('coffee_quiz_email_submitted');
    
    // Reset UI elements
    this.modal.querySelectorAll('input[type="radio"]').forEach(input => {
      input.checked = false;
    });
    this.modal.querySelectorAll('input[type="checkbox"]').forEach(input => {
      input.checked = false;
    });
    this.modal.querySelectorAll('.is-selected').forEach(el => {
      el.classList.remove('is-selected');
    });
    
    // Reset slider
    const slider = this.modal.querySelector('[data-cups-slider]');
    if (slider) {
      slider.value = 1;
      this.updateSliderDisplay(1);
    }
    
    // Reset results display
    const loadingEl = this.modal.querySelector('.coffee-quiz__product-loading');
    const productCard = this.modal.querySelector('.coffee-quiz__product-card');
    const purchaseOptions = this.modal.querySelector('.coffee-quiz__purchase-options');
    const resultsFooter = this.modal.querySelector('.coffee-quiz__results-footer');
    if (loadingEl) loadingEl.style.display = 'block';
    if (productCard) {
      productCard.style.display = 'none';
      productCard.classList.remove('animate-in');
    }
    if (purchaseOptions) {
      purchaseOptions.style.display = 'none';
      purchaseOptions.classList.remove('animate-in');
    }
    if (resultsFooter) {
      resultsFooter.classList.remove('animate-in');
    }
    
    // Reset size and quantity selections
    this.modal.querySelectorAll('.coffee-quiz__size-option, .coffee-quiz__quantity-option').forEach(btn => {
      btn.classList.remove('is-selected');
    });
    this.modal.querySelectorAll('.coffee-quiz__size-option[data-size="12oz"], .coffee-quiz__quantity-option[data-quantity="1"]').forEach(btn => {
      btn.classList.add('is-selected');
    });
    
    // Go back to intro
    this.goToScreen(0);
    
    // Track restart
    this.trackEvent('quiz_restarted', { quiz_name: 'coffee_quiz' });
  }

  /**
   * Update progress bar
   */
  updateProgress() {
    const progressBar = this.modal.querySelector('[data-quiz-progress]');
    const progressContainer = this.modal.querySelector('[data-quiz-progress-container]');
    if (!progressBar) return;
    
    // Don't count intro and results in progress
    const totalSteps = this.screens.length - 2; // Exclude intro and results
    const currentStep = Math.max(0, this.currentScreenIndex - 1);
    const progress = this.currentScreenIndex === 0 ? 0 : 
                     this.currentScreenIndex >= this.screens.length - 1 ? 100 :
                     (currentStep / totalSteps) * 100;
    
    progressBar.style.width = `${progress}%`;
    
    // Update ARIA attributes for screen readers
    if (progressContainer) {
      progressContainer.setAttribute('aria-valuenow', Math.round(progress));
      progressContainer.setAttribute('aria-valuetext', `Quiz progress: ${Math.round(progress)}% complete`);
    }
  }

  /**
   * Calculate and display product recommendation
   */
  calculateRecommendation() {
    console.log('%c╔══════════════════════════════════════════════╗', 'color: #00ff00; font-weight: bold;');
    console.log('%c║      QUIZ RECOMMENDATION DEBUG               ║', 'color: #00ff00; font-weight: bold;');
    console.log('%c╚══════════════════════════════════════════════╝', 'color: #00ff00; font-weight: bold;');
    
    console.log('%c📋 USER ANSWERS:', 'color: #ff9900; font-weight: bold;');
    console.log('  - Caffeine Preference:', this.answers.caffeinePreference);
    console.log('  - Flavors:', this.answers.flavors);
    console.log('  - Body Preference:', this.answers.bodyPreference);
    console.log('  - Brew Method:', this.answers.brewMethod);
    console.log('  - Experience:', this.answers.experience);
    console.log('  - Cups Per Day:', this.answers.cupsPerDay);
    console.log('  Full answers object:', JSON.parse(JSON.stringify(this.answers)));
    
    console.log('%c📦 PRODUCTS:', 'color: #ff9900; font-weight: bold;');
    console.log('Total products loaded:', this.products.length);
    
    if (this.products.length === 0) {
      console.log('%c❌ ERROR: No products loaded!', 'color: red; font-weight: bold;');
      return;
    }
    
    // Show first product's metafield structure for debugging
    if (this.products.length > 0) {
      const sampleProduct = this.products[0];
      console.log('%c🔍 SAMPLE PRODUCT METAFIELDS (first product):', 'color: #9966ff; font-weight: bold;');
      console.log('  Product:', sampleProduct.title);
      console.log('  Raw quizFlavors:', sampleProduct.quizFlavors, '(type:', typeof sampleProduct.quizFlavors, ')');
      console.log('  Raw quizBody:', sampleProduct.quizBody, '(type:', typeof sampleProduct.quizBody, ')');
      console.log('  Raw quizBrewMethods:', sampleProduct.quizBrewMethods, '(type:', typeof sampleProduct.quizBrewMethods, ')');
    }

    // Exclude sampler products and out-of-stock products from recommendations
    const excludedHandles = ['house-blend-coffee-sampler', 'coffee-sampler', 'sampler'];
    const baseEligible = this.products.filter(product => {
      // Exclude out-of-stock products
      if (!product.available) return false;

      // Exclude sampler products
      const handleLower = product.handle.toLowerCase();
      const titleLower = product.title.toLowerCase();
      const isSampler = excludedHandles.some(exc => handleLower.includes(exc)) ||
                        titleLower.includes('sampler');
      if (isSampler) return false;

      return true;
    });

    // Caffeine preference is a hard filter (a decaf seeker should never get a caffeinated coffee).
    // Fall back to the unfiltered set if nothing matches, so the quiz always returns something.
    const caffeineMatched = baseEligible.filter(product => this.matchesCaffeinePreference(product));
    const eligibleProducts = caffeineMatched.length > 0 ? caffeineMatched : baseEligible;

    console.log('Eligible products after filtering:', eligibleProducts.length,
      caffeineMatched.length === 0 ? '(caffeine filter relaxed — no exact matches)' : '');

    // Score each product
    console.log('%c📊 SCORING PRODUCTS:', 'color: #ff9900; font-weight: bold;');
    
    const scoredProducts = eligibleProducts.map(product => {
      let score = 0;
      const matchedTags = [];
      const scoreBreakdown = []; // Track how score is built
      const debugInfo = {
        title: product.title,
        quizFlavors: product.quizFlavors,
        quizBody: product.quizBody,
        quizBrewMethods: product.quizBrewMethods
      };
      
      // Flat 1-point scoring: every matching answer is worth exactly 1 point.
      // Caffeine preference and limited-time eligibility are handled as hard filters
      // upstream, so they don't contribute points here.

      // Caffeine: surface a tag for the matched preference (no points — already filtered).
      const caffeineTag = this.getCaffeineTag();
      if (caffeineTag) matchedTags.push(caffeineTag);

      // Experience: an advanced drinker scores +1 on rare / non-subscription coffees
      // (those without selling plans) when the merchant includes them in the collection.
      if (this.answers.experience === 'advanced' && this.isLimitedTimeProduct(product)) {
        score += 1;
        matchedTags.push('Rare & Exclusive');
        scoreBreakdown.push('Experience: +1 (advanced + limited-time coffee)');
      }

      // Flavors: +1 for each selected flavor note this coffee actually has.
      const productFlavors = this.normalizeMetafieldList(product.quizFlavors);

      scoreBreakdown.push(`Flavor metafield raw: ${JSON.stringify(product.quizFlavors)}`);
      scoreBreakdown.push(`Flavor metafield normalized: [${productFlavors.join(', ')}]`);

      if (this.answers.flavors && productFlavors.length > 0) {
        this.answers.flavors.forEach(flavor => {
          const flavorKey = flavor.replace(/-/g, ' ').toLowerCase();
          const hasFlavor = productFlavors.some(pf => pf.includes(flavorKey) || flavorKey.includes(pf));

          if (hasFlavor) {
            score += 1;
            matchedTags.push(this.capitalizeFirst(flavor.replace(/-/g, ' ')));
            scoreBreakdown.push(`Flavor: +1 (matched "${flavor}")`);
          }
        });
      } else if (this.answers.flavors && productFlavors.length === 0) {
        scoreBreakdown.push('Flavor: no metafield data on product');
      }

      // Body: +1 if the coffee's body matches the customer's preference.
      const productBody = this.normalizeMetafieldValue(product.quizBody);

      scoreBreakdown.push(`Body metafield raw: ${JSON.stringify(product.quizBody)}`);
      scoreBreakdown.push(`Body metafield normalized: "${productBody}"`);

      if (this.answers.bodyPreference && this.answers.bodyPreference !== 'i-dont-know' && productBody) {
        const userBodyPref = this.answers.bodyPreference.replace('-body', '').toLowerCase();

        if (productBody === userBodyPref || productBody.includes(userBodyPref)) {
          score += 1;
          matchedTags.push(this.capitalizeFirst(this.answers.bodyPreference.replace(/-/g, ' ')));
          scoreBreakdown.push(`Body: +1 (matched "${userBodyPref}" with "${productBody}")`);
        } else {
          scoreBreakdown.push(`Body: no match ("${userBodyPref}" vs "${productBody}")`);
        }
      } else if (this.answers.bodyPreference && this.answers.bodyPreference !== 'i-dont-know' && !productBody) {
        scoreBreakdown.push('Body: no metafield data on product');
      }

      // Brew method: +1 if the coffee supports the customer's brew method.
      const productBrewMethods = this.normalizeMetafieldList(product.quizBrewMethods);

      scoreBreakdown.push(`Brew metafield raw: ${JSON.stringify(product.quizBrewMethods)}`);
      scoreBreakdown.push(`Brew metafield normalized: [${productBrewMethods.join(', ')}]`);

      if (this.answers.brewMethod && productBrewMethods.length > 0) {
        const userBrewMethod = this.answers.brewMethod.toLowerCase().replace(/-/g, ' ');

        const hasBrewMatch = productBrewMethods.some(bm =>
          bm.includes(userBrewMethod) || userBrewMethod.includes(bm)
        );

        if (hasBrewMatch) {
          score += 1;
          matchedTags.push(this.capitalizeFirst(this.answers.brewMethod.replace(/-/g, ' ')));
          scoreBreakdown.push(`Brew: +1 (matched "${userBrewMethod}" with product brew methods)`);
        } else {
          scoreBreakdown.push(`Brew: no match ("${userBrewMethod}" vs [${productBrewMethods.join(', ')}])`);
        }
      } else if (this.answers.brewMethod && productBrewMethods.length === 0) {
        scoreBreakdown.push('Brew: no metafield data on product');
      }

      return { product, score, matchedTags, debugInfo, scoreBreakdown };
    });
    
    // Sort by score and get the best match
    scoredProducts.sort((a, b) => b.score - a.score);
    
    // Log top 5 products for debugging
    console.log('%c🏆 TOP 5 SCORED PRODUCTS:', 'color: #00ff00; font-weight: bold; font-size: 14px;');
    scoredProducts.slice(0, 5).forEach((item, i) => {
      console.log(`%c${i + 1}. ${item.product.title}: Score = ${item.score}`, 'color: #ffcc00; font-weight: bold;');
      console.log('   Matched Tags:', item.matchedTags);
      console.log('   Score Breakdown:');
      item.scoreBreakdown.forEach(line => console.log(`      ${line}`));
    });
    
    const best = scoredProducts[0];
    
    if (!best) {
      console.log('%c❌ ERROR: No best product found!', 'color: red; font-weight: bold;');
      return;
    }
    
    console.log('%c✅ RECOMMENDED: ' + best.product.title + ' (Score: ' + best.score + ')', 'background: #00aa00; color: white; font-size: 14px; padding: 4px 8px;');
    
    this.recommendedProduct = best.product;
    this.displayRecommendation(best.product, best.matchedTags);
    this.setRecommendedQuantityAndFrequency();
  }

  /**
   * A limited-time / rare coffee is sold as a one-time purchase only (no subscription).
   * This is determined by an explicit merchant-controlled product tag rather than by the
   * absence of selling-plan data, which proved unreliable (subscriptions on this store are
   * not always exposed as native Shopify selling plans, so an empty `sellingPlans` array
   * does NOT mean a coffee can't be subscribed to). Tag a product `limited-time` (or
   * `one-time-only`) to present it as a single, centered one-time purchase option.
   * @param {Object} product
   * @returns {boolean}
   */
  isLimitedTimeProduct(product) {
    const tags = product.tags || [];
    return tags.some(tag => {
      const normalized = String(tag).trim().toLowerCase();
      return normalized === 'limited-time' ||
             normalized === 'limited time' ||
             normalized === 'one-time-only' ||
             normalized === 'one time only';
    });
  }

  /**
   * Whether a product is decaf based on its tags.
   * @param {Object} product
   * @returns {boolean}
   */
  isDecafProduct(product) {
    return product.tags.some(t => t.toLowerCase().includes('decaf'));
  }

  /**
   * Whether a product is half-caff based on handle, title, or tags.
   * @param {Object} product
   * @returns {boolean}
   */
  isHalfCaffProduct(product) {
    return product.handle.toLowerCase().includes('miti') ||
           product.title.toLowerCase().includes('half caff') ||
           product.tags.some(t => t.toLowerCase().includes('half caff') || t.toLowerCase().includes('half-caff'));
  }

  /**
   * Hard filter for the caffeine question. A product is eligible only if it matches
   * the customer's caffeine preference (decaf / half-caff / caffeinated).
   * @param {Object} product
   * @returns {boolean}
   */
  matchesCaffeinePreference(product) {
    const preference = this.answers.caffeinePreference;
    if (!preference) return true;

    const isDecaf = this.isDecafProduct(product);
    const isHalfCaff = this.isHalfCaffProduct(product);

    if (preference === 'decaf') return isDecaf;
    if (preference === 'half-caff') return isHalfCaff;
    if (preference === 'caffeinated') return !isDecaf && !isHalfCaff;
    return true;
  }

  /**
   * Display tag for the matched caffeine preference (cosmetic only — no scoring).
   * @returns {string} Tag label, or '' if no caffeine preference was set
   */
  getCaffeineTag() {
    const preference = this.answers.caffeinePreference;
    if (preference === 'decaf') return 'Decaf';
    if (preference === 'half-caff') return 'Half Caff';
    if (preference === 'caffeinated') return 'Caffeinated';
    return '';
  }

  /**
   * Normalize metafield value to a lowercase string
   * Handles: string, array, object, null/undefined
   */
  normalizeMetafieldValue(value) {
    if (!value) return '';
    
    // If it's an array, join and lowercase
    if (Array.isArray(value)) {
      return value.map(v => String(v).toLowerCase().trim()).join(', ');
    }
    
    // If it's an object, try to get a value property or stringify
    if (typeof value === 'object') {
      return String(value.value || value.name || JSON.stringify(value)).toLowerCase().trim();
    }
    
    // Otherwise treat as string
    return String(value).toLowerCase().trim();
  }

  /**
   * Normalize metafield to an array of lowercase strings
   * Handles: string (comma-separated), array, null/undefined
   */
  normalizeMetafieldList(value) {
    if (!value) return [];
    
    // If it's already an array, normalize each item
    if (Array.isArray(value)) {
      return value.map(v => String(v).toLowerCase().trim()).filter(v => v);
    }
    
    // If it's an object, try to extract array or value
    if (typeof value === 'object' && !Array.isArray(value)) {
      // Handle Shopify metafield object format
      const actualValue = value.value || value;
      if (Array.isArray(actualValue)) {
        return actualValue.map(v => String(v).toLowerCase().trim()).filter(v => v);
      }
      return [String(actualValue).toLowerCase().trim()].filter(v => v);
    }
    
    // Otherwise treat as comma-separated string
    return String(value).toLowerCase().split(',').map(v => v.trim()).filter(v => v);
  }

  /**
   * Get user's quiz selections as display tags
   */
  getUserSelectionTags() {
    const tags = [];
    
    // Add brew method
    if (this.answers.brewMethod) {
      tags.push(this.capitalizeFirst(this.answers.brewMethod.replace(/-/g, ' ')));
    }
    
    // Add flavors
    if (this.answers.flavors && Array.isArray(this.answers.flavors)) {
      this.answers.flavors.forEach(flavor => {
        tags.push(this.capitalizeFirst(flavor.replace(/-/g, ' ')));
      });
    }
    
    // Add body preference (if not "don't know")
    if (this.answers.bodyPreference && this.answers.bodyPreference !== 'i-dont-know') {
      tags.push(this.capitalizeFirst(this.answers.bodyPreference.replace(/-/g, ' ')));
    }
    
    // Add caffeine preference
    if (this.answers.caffeinePreference) {
      tags.push(this.capitalizeFirst(this.answers.caffeinePreference.replace(/-/g, ' ')));
    }
    
    return tags;
  }

  /**
   * Capitalize first letter of each word
   */
  capitalizeFirst(str) {
    return str.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  /**
   * Extract intro description (everything before "Coffee Description")
   */
  extractNotes(description) {
    if (!description) return '';
    
    // Strip HTML and normalize whitespace
    const cleanDesc = description
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<\/div>/gi, ' ')
      .replace(/<\/span>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&#?\w+;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Extract everything before "Coffee Description"
    const coffeeDescIndex = cleanDesc.toLowerCase().indexOf('coffee description');
    if (coffeeDescIndex > 0) {
      return cleanDesc.substring(0, coffeeDescIndex).trim();
    }
    
    // Fallback: first paragraph
    const firstParagraph = cleanDesc.split(/(?:\.\s+[A-Z]|\n)/)[0];
    if (firstParagraph?.length > 20) {
      return firstParagraph.trim() + (firstParagraph.endsWith('.') ? '' : '.');
    }
    
    // Last resort: truncate
    return cleanDesc.length > 150 ? cleanDesc.substring(0, 150).trim() + '...' : cleanDesc;
  }
  
  /**
   * Display the recommended product
   */
  displayRecommendation(product, matchedTags) {
    // Show product card and purchase options, hide loading
    const loadingEl = this.modal.querySelector('.coffee-quiz__product-loading');
    const productCard = this.modal.querySelector('.coffee-quiz__product-card');
    const purchaseOptions = this.modal.querySelector('.coffee-quiz__purchase-options');
    const resultsFooter = this.modal.querySelector('.coffee-quiz__results-footer');
    
    if (loadingEl) loadingEl.style.display = 'none';
    
    // Show elements with animation classes
    if (productCard) {
      productCard.style.display = 'block';
      productCard.classList.add('animate-in');
    }
    if (purchaseOptions) {
      purchaseOptions.style.display = 'grid';
      purchaseOptions.classList.add('animate-in');
    }
    if (resultsFooter) {
      resultsFooter.classList.add('animate-in');
    }

    // Limited-time coffees can't be subscribed to — hide the subscription option and
    // present the one-time purchase as the recommended (and only) choice.
    const isLimited = this.isLimitedTimeProduct(product);
    const subscriptionOption = this.modal.querySelector('[data-purchase-type="subscription"]');
    const oneTimeOption = this.modal.querySelector('[data-purchase-type="onetime"]');
    if (subscriptionOption) {
      subscriptionOption.hidden = isLimited;
    }
    if (oneTimeOption) {
      // When subscriptions are unavailable, the one-time card becomes the recommended option.
      oneTimeOption.classList.toggle('is-recommended', isLimited);
    }
    if (purchaseOptions) {
      purchaseOptions.classList.toggle('coffee-quiz__purchase-options--single', isLimited);
    }

    // Update the usage note (inside subscription option)
    const usageNote = this.modal.querySelector('[data-usage-note]');
    if (usageNote) {
      const cupsPerDay = parseInt(this.answers.cupsPerDay) || 1;
      const cupsSummary = usageNote.querySelector('[data-cups-summary]');
      if (cupsSummary) {
        cupsSummary.textContent = cupsPerDay === 1 ? '1 cup' : `${cupsPerDay} cups`;
      }
    }
    
    // Update product info
    const imageEl = this.modal.querySelector('[data-quiz-product-image]');
    const nameEl = this.modal.querySelector('[data-quiz-product-name]');
    const notesEl = this.modal.querySelector('[data-quiz-product-notes]');
    const tagsContainer = this.modal.querySelector('[data-quiz-matched-tags]');
    const ratingContainer = this.modal.querySelector('[data-quiz-product-rating]');
    const reviewsWidgetContainer = this.modal.querySelector('[data-quiz-product-reviews-widget]');
    
    if (imageEl) {
      imageEl.src = product.image;
      imageEl.alt = product.title;
      
      // Wrap image in link to PDP (opens in new window)
      const imageLink = this.modal.querySelector('[data-quiz-product-image-link]');
      if (imageLink) {
        imageLink.href = product.url;
        imageLink.target = '_blank';
        imageLink.rel = 'noopener';
      }
    }
    
    if (nameEl) {
      nameEl.textContent = product.title;
    }
    
    // Display quiz description from metafield (or fallback to extractNotes)
    if (notesEl) {
      notesEl.textContent = product.quizDescription || this.extractNotes(product.description);
    }
    
    // Set "More Info" link URL
    const moreInfoLink = this.modal.querySelector('[data-quiz-product-link]');
    if (moreInfoLink) {
      moreInfoLink.href = product.url;
    }
    
    // Render the same Klaviyo reviews component used in product contexts
    if (ratingContainer && reviewsWidgetContainer && product.id) {
      ratingContainer.hidden = false;
      reviewsWidgetContainer.textContent = '';

      const reviewsWidget = document.createElement('div');
      reviewsWidget.className = 'klaviyo-star-rating-widget';
      reviewsWidget.setAttribute('data-id', String(product.id));
      reviewsWidget.setAttribute('data-product-title', product.title || '');
      reviewsWidget.setAttribute('data-product-type', product.type || '');
      reviewsWidgetContainer.appendChild(reviewsWidget);
    } else if (ratingContainer) {
      ratingContainer.hidden = true;
    }
    
    // Display user selections as tags
    if (tagsContainer) {
      const userSelections = this.getUserSelectionTags();
      tagsContainer.innerHTML = userSelections.map(tag => 
        `<span class="coffee-quiz__matched-tag">${tag}</span>`
      ).join('');
    }
    
    // Default grind to Whole Bean
    this.modal.querySelectorAll('[data-subscription-grind], [data-onetime-grind]').forEach(select => {
      select.value = 'whole-bean';
    });
    
    // Update pricing
    this.updatePricing();
    
    // Track recommendation
    this.trackEvent('quiz_product_recommended', {
      product_id: product.id,
      product_title: product.title,
      matched_tags: matchedTags
    });
  }

  /**
   * Set recommended quantity and frequency based on cups per day
   */
  setRecommendedQuantityAndFrequency() {
    const cupsPerDay = parseInt(this.answers.cupsPerDay) || 1;
    let recommendedQty = 1;
    let recommendedFrequency = '2-weeks'; // Default to 2 weeks
    
    // Calculate based on cups per day (12oz bag = ~17-21 cups, use 19 as average)
    // cups/day * 7 days = cups/week, bags needed = cups/week / 19
    if (cupsPerDay <= 1) {
      recommendedQty = 1;
      recommendedFrequency = '3-weeks';
    } else if (cupsPerDay <= 3) {
      recommendedQty = 1;
      recommendedFrequency = '2-weeks';
    } else if (cupsPerDay <= 5) {
      recommendedQty = 2;
      recommendedFrequency = '2-weeks';
    } else if (cupsPerDay <= 6) {
      recommendedQty = 2;
      recommendedFrequency = '1-week';
    } else {
      recommendedQty = 3;
      recommendedFrequency = '2-weeks';
    }
    
    // Update subscription quantity
    this.modal.querySelectorAll('[data-subscription-quantity]').forEach(btn => {
      const isSelected = btn.dataset.quantity === String(recommendedQty);
      btn.classList.toggle('is-selected', isSelected);
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
    
    // Update subscription frequency
    const frequencySelect = this.modal.querySelector('[data-subscription-frequency]');
    if (frequencySelect) {
      frequencySelect.value = recommendedFrequency;
    }
    
    this.updatePricing();
  }

  /**
   * Find a variant matching the chosen size + grind, falling back to the first variant.
   */
  findVariant(size, grind) {
    const variants = this.recommendedProduct.variants || [];
    if (variants.length === 0) return null;

    const sizeMatchers = this.getSizeMatchers(size);
    const grindMatchers = this.getGrindMatchers(grind);

    const match = variants.find(v => {
      const options = v.options.map(o => String(o).toLowerCase().replace(/\s+/g, ''));
      const sizeOk = options.some(o => sizeMatchers.some(s => o.includes(s)));
      const grindOk = grindMatchers.length === 0
        ? true
        : options.some(o => grindMatchers.some(g => o.includes(g)));
      return sizeOk && grindOk;
    });

    return match || variants[0];
  }

  getSizeMatchers(size) {
    if (size === '2.5kg') return ['2.5kg', '2,5kg', '2.5k', '2500g'];
    return ['340g', '12oz'];
  }

  getGrindMatchers(grind) {
    const map = {
      'whole-bean': ['grano', 'wholebean', 'wholebean', 'engrano'],
      'drip': ['cafeteraautomatica', 'drip', 'automatica'],
      'pour-over': ['aeropress', 'pourover', 'dripper', 'coldbrew'],
      'french-press': ['prensafrancesa', 'frenchpress', 'chemex', 'cafeteramoka', 'moka'],
      'espresso': ['espresso']
    };
    return map[grind] || [];
  }

  /**
   * Get the Subify-style frequency label (matches dropdown text).
   * E.g. '2-weeks' -> 'Cada 2 semanas', '1-week' -> 'Cada semana'.
   */
  getFrequencyLabel(value) {
    const select = this.modal.querySelector('[data-subscription-frequency]');
    if (select) {
      const opt = select.querySelector(`option[value="${value}"]`);
      if (opt) return opt.textContent.trim();
    }
    const weeks = parseInt(value, 10) || 2;
    return weeks === 1 ? 'Cada semana' : `Cada ${weeks} semanas`;
  }

  /**
   * Resolve the per-bag price for a given size by looking up the variant,
   * so 2.5kg pricing isn't a guess.
   */
  getBagPrice(size, grind) {
    const variant = this.findVariant(size, grind);
    if (variant && typeof variant.price === 'number') return variant.price;
    return this.recommendedProduct.price;
  }

  /**
   * Update pricing display
   */
  updatePricing() {
    if (!this.recommendedProduct) return;

    const subSize = this.modal.querySelector('[data-subscription-size].is-selected')?.dataset.size || '340g';
    const subQty = parseInt(this.modal.querySelector('[data-subscription-quantity].is-selected')?.dataset.quantity) || 1;
    const subGrind = this.modal.querySelector('[data-subscription-grind]')?.value || 'whole-bean';

    const otSize = this.modal.querySelector('[data-onetime-size].is-selected')?.dataset.size || '340g';
    const otQty = parseInt(this.modal.querySelector('[data-onetime-quantity].is-selected')?.dataset.quantity) || 1;
    const otGrind = this.modal.querySelector('[data-onetime-grind]')?.value || 'whole-bean';

    // Variant prices come back from Shopify in cents — divide once at display time.
    const subBagPrice = this.getBagPrice(subSize, subGrind) / 100;
    const otBagPrice = this.getBagPrice(otSize, otGrind) / 100;

    const subOriginal = subBagPrice * subQty;
    const subSavings = subOriginal * SUBSCRIPTION_DISCOUNT;
    const subTotal = subOriginal - subSavings;
    const discountPercent = Math.round(SUBSCRIPTION_DISCOUNT * 100);

    const otSubtotal = otBagPrice * otQty;
    const otTotal = otSubtotal;

    const fmt = (n) => `$${n.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

    // Subscription pricing — show original (strikethrough), savings, discounted total
    const subOriginalEl = this.modal.querySelector('[data-subscription-original]');
    const subSubtotalEl = this.modal.querySelector('[data-subscription-subtotal]');
    const subSavingsEl = this.modal.querySelector('[data-subscription-savings]');
    const subDiscountLabelEl = this.modal.querySelector('[data-subscription-discount-label]');
    const subTotalEl = this.modal.querySelector('[data-subscription-total]');

    if (subOriginalEl) subOriginalEl.textContent = fmt(subOriginal);
    if (subSubtotalEl) subSubtotalEl.textContent = fmt(subOriginal);
    if (subSavingsEl) subSavingsEl.textContent = `-${fmt(subSavings)}`;
    if (subDiscountLabelEl) subDiscountLabelEl.textContent = `Descuento suscripción (${discountPercent}%)`;
    if (subTotalEl) subTotalEl.textContent = fmt(subTotal);

    const otSubtotalEl = this.modal.querySelector('[data-onetime-subtotal]');
    const otTotalEl = this.modal.querySelector('[data-onetime-total]');
    if (otSubtotalEl) otSubtotalEl.textContent = fmt(otSubtotal);
    if (otTotalEl) otTotalEl.textContent = fmt(otTotal);
  }

  /**
   * Add product to cart
   */
  async addToCart(type) {
    if (this.isAddingToCart || !this.recommendedProduct) return;
    
    this.isAddingToCart = true;
    
    const isSubscription = type === 'subscription';
    const prefix = isSubscription ? 'subscription' : 'onetime';
    
    // Disable the button while processing. Capture the original markup so it can
    // be restored afterwards — the results page DOM persists between quiz runs,
    // so a button left in the "Adding..." state would stay stuck and block a
    // re-add when the same recommendation is shown again.
    const addButton = this.modal.querySelector(`[data-add-${type === 'subscription' ? 'subscription' : 'onetime'}]`);
    const originalButtonHtml = addButton?.innerHTML;
    if (addButton) {
      addButton.disabled = true;
      addButton.textContent = 'Adding...';
    }
    
    // Get selected options
    const size = this.modal.querySelector(`[data-${prefix}-size].is-selected`)?.dataset.size || '340g';
    const grind = this.modal.querySelector(`[data-${prefix}-grind]`)?.value || 'whole-bean';
    const quantity = parseInt(this.modal.querySelector(`[data-${prefix}-quantity].is-selected`)?.dataset.quantity) || 1;

    // Find matching variant by size + grind options
    const variant = this.findVariant(size, grind);

    // Build the request as application/x-www-form-urlencoded, matching the PDP's
    // payload byte-for-byte. The Moxie subscription app (suscripciones-moxie.onrender.com)
    // + the merchant's Shopify Function read these line-item properties to apply
    // the "Descuento suscripción" automatic discount at checkout. JSON / items-array
    // requests don't trigger the Function reliably — see HAR trace for cafe-toro.
    const params = new URLSearchParams();
    params.append('id', String(variant.id));
    params.append('quantity', String(quantity));
    params.append('selling_plan', '');

    if (isSubscription) {
      const frequencyValue = this.modal.querySelector('[data-subscription-frequency]')?.value || '2-weeks';
      const frequencyLabel = this.getFrequencyLabel(frequencyValue);
      const weeks = parseInt(frequencyValue, 10) || 2;
      // Per-unit price in shop currency (variant.price is in subunits — divide by 100).
      const discountedUnitPrice = Math.round((variant.price / 100) * (1 - SUBSCRIPTION_DISCOUNT));
      const discountPercent = Math.round(SUBSCRIPTION_DISCOUNT * 100);

      params.append('properties[Tipo de compra]', 'Suscripción');
      params.append('properties[Frecuencia de entrega]', frequencyLabel);
      params.append('properties[Unidad de frecuencia]', 'WEEK');
      params.append('properties[Cantidad de frecuencia]', String(weeks));
      params.append('properties[Suscripción]', `${discountPercent}% OFF`);
      params.append('properties[Precio con descuento]', String(discountedUnitPrice));
      params.append('properties[Descuento aplicado]', `${discountPercent}%`);
    }

    // Ask /cart/add.js to return the re-rendered cart-drawer section(s) so the
    // drawer can morph from HTML that already reflects the new line. Without this
    // the bare re-render fallback could show a stale cart whose subscription line
    // isn't wired for the cadence <select> / "Compra única" toggle. Mirrors the
    // PDP add (awp_product_form.js #cartSectionIds).
    const cartSectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId && cartSectionIds.indexOf(el.dataset.sectionId) === -1) {
        cartSectionIds.push(el.dataset.sectionId);
      }
    });
    if (cartSectionIds.length > 0) {
      params.append('sections', cartSectionIds.join(','));
    }

    this.trackEvent('quiz_add_to_cart', {
      product_id: this.recommendedProduct.id,
      variant_id: variant.id,
      purchase_type: type,
      quantity: quantity
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'Accept': '*/*'
        },
        body: params.toString()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const cartData = await response.json();
      this.close();

      // Refresh + open the native cart drawer. Pass the rendered sections from the
      // add response so cart-items-component morphs the drawer in place (falling
      // back to a server re-render only if sections are absent), then explicitly
      // open the drawer so the new line shows immediately.
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { source: 'coffee-quiz', sections: cartData?.sections } }
      }));
      const cartDrawer = document.querySelector('cart-drawer-component') || document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();

    } catch (error) {
      console.error('Coffee Quiz: Error adding to cart:', error);
      alert('Sorry, there was an error adding to your cart. Please try again.');
    } finally {
      // Reset the button on both success and error so the results page can be
      // re-used to add the same product again on a later quiz run.
      if (addButton) {
        addButton.disabled = false;
        if (originalButtonHtml != null) addButton.innerHTML = originalButtonHtml;
      }
      this.isAddingToCart = false;
    }
  }

  /**
   * Track step completion
   */
  trackStepCompleted(stepName, answer) {
    const stepIndex = this.screens.indexOf(
      this.screens.find(s => s.includes(stepName.replace(/([A-Z])/g, '-$1').toLowerCase()))
    ) || this.currentScreenIndex;
    
    this.trackEvent('quiz_step_completed', {
      step: stepIndex,
      step_name: stepName,
      answer: answer
    });
  }

  /**
   * Track GA4 event
   */
  trackEvent(eventName, params = {}) {
    if (typeof gtag === 'function') {
      gtag('event', eventName, { quiz_name: 'coffee_quiz', ...params });
    }
    if (typeof dataLayer !== 'undefined') {
      dataLayer.push({ event: eventName, ...params });
    }
  }
}

/**
 * Check if quiz is enabled in theme editor
 * Returns true if not in editor, or if enable_in_editor setting is true
 */
function isQuizEnabledInEditor() {
  // Always enabled outside of theme editor
  if (!window.Shopify?.designMode) return true;

  const modal = document.getElementById('coffee-quiz-modal');
  if (!modal) return false;

  // Check the enable_in_editor data attribute
  const enableInEditor = modal.getAttribute('data-enable-in-editor');
  return enableInEditor !== 'false';
}

// Initialize quiz when DOM is ready
function initCoffeeQuiz(forceNew = false) {
  // Skip initialization in theme editor if disabled
  if (window.Shopify?.designMode && !isQuizEnabledInEditor()) {
    console.log('Coffee Quiz: Disabled in theme editor');
    if (window.coffeeQuiz?.modal?.open) {
      window.coffeeQuiz.close();
    }
    window.coffeeQuiz = null;
    return;
  }
  
  if (forceNew || (window.Shopify && window.Shopify.designMode)) {
    window.coffeeQuiz = new CoffeeQuiz();
    return;
  }
  if (!window.coffeeQuiz) {
    window.coffeeQuiz = new CoffeeQuiz();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCoffeeQuiz());
} else {
  initCoffeeQuiz();
}

// Handle Shopify theme editor section reload
document.addEventListener('shopify:section:load', (e) => {
  const modal = e.target?.querySelector('#coffee-quiz-modal');
  if (!modal) return;

  // Skip if disabled in editor
  const enableInEditor = modal.getAttribute('data-enable-in-editor') !== 'false';
  if (window.Shopify?.designMode && !enableInEditor) {
    console.log('Coffee Quiz: Disabled in theme editor, skipping reload');
    if (window.coffeeQuiz?.modal?.open) {
      window.coffeeQuiz.close();
    }
    window.coffeeQuiz = null;
    return;
  }
  
  setTimeout(() => {
    const latestModal = document.getElementById('coffee-quiz-modal');
    if (window.Shopify?.designMode && latestModal?.getAttribute('data-enable-in-editor') === 'false') {
      if (window.coffeeQuiz?.modal?.open) {
        window.coffeeQuiz.close();
      }
      window.coffeeQuiz = null;
      return;
    }

    initCoffeeQuiz(true);
    if (window.coffeeQuiz && window.Shopify?.designMode) {
      window.coffeeQuiz.restoreEditorPosition();
    }
  }, 150);
});

// Handle theme editor preview screen changes
document.addEventListener('shopify:section:rerender', (e) => {
  const modal = e.target?.querySelector('#coffee-quiz-modal');
  if (!modal || !window.coffeeQuiz) return;
  
  // Skip if disabled in editor
  if (window.Shopify?.designMode && modal.getAttribute('data-enable-in-editor') === 'false') {
    if (window.coffeeQuiz.modal?.open) {
      window.coffeeQuiz.close();
    }
    window.coffeeQuiz = null;
    return;
  }
  
  const previewScreen = modal.dataset.previewScreen;
  if (previewScreen && previewScreen !== 'none') {
    const screenIndex = window.coffeeQuiz.screens.indexOf(previewScreen);
    if (screenIndex !== -1) {
      window.coffeeQuiz.modal.showModal();
      document.body.classList.add('coffee-quiz-open');
      window.coffeeQuiz.goToScreen(screenIndex);
    }
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CoffeeQuiz;
}
